import type { ReactElement } from 'react';
import type { UserType } from '@kirin/api-client/contracts/user';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  GUEST_DESCRIPTION,
  GUEST_LABEL,
} from '@/pages/organization/tabs/DataTableEndpointsTab/constants';

export interface UserTypeTagProps {
  userType?: UserType | null;
  /**
   * KIR-5410: the person has a name (a shared-device OS account), so the
   * label above already reads it and an Unattributed person is tagged
   * "Unattributed" rather than "No email".
   */
  named?: boolean;
  /** KIR-5410: the person is offboarded from the organization. */
  offboarded?: boolean;
  /** KIR-5696: an Unattributed person that is a computer's Guest (KIR-5670). */
  guest?: boolean;
  className?: string;
}

/**
 * KIR-3908: small tag next to a user in the Alerts views.
 * - `discovered` → "Discovered" (a Cowork user with no Kirin account).
 * - `unattributed` → "No email" (the per-org placeholder for a missing/invalid
 *   acting email; the label itself already reads "Unattributed"), or
 *   "Unattributed" when the person has an OS account name (KIR-5410), or
 *   "Guest" for a computer's Guest, as on the endpoint page (KIR-5696).
 * - offboarded → "Offboarded" (KIR-5410).
 * `full` (or absent) renders nothing.
 */
export function UserTypeTag({
  userType,
  named = false,
  offboarded = false,
  guest = false,
  className,
}: UserTypeTagProps): ReactElement | null {
  if (offboarded) {
    return (
      <Badge
        variant="outline"
        className={cn(
          'border-red-300 text-red-700 dark:border-red-800 dark:text-red-300',
          className
        )}
      >
        Offboarded
      </Badge>
    );
  }
  if (userType === 'discovered') {
    return (
      <Badge
        variant="outline"
        className={cn(
          'border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-300',
          className
        )}
      >
        Discovered
      </Badge>
    );
  }
  if (userType === 'unattributed' && guest) {
    return (
      <Badge
        variant="outline"
        title={GUEST_DESCRIPTION}
        className={cn('text-muted-foreground', className)}
      >
        {GUEST_LABEL}
      </Badge>
    );
  }
  if (userType === 'unattributed') {
    return (
      <Badge
        variant="outline"
        className={cn('text-muted-foreground', className)}
      >
        {named ? 'Unattributed' : 'No email'}
      </Badge>
    );
  }
  return null;
}

UserTypeTag.displayName = 'UserTypeTag';
